// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Etage as a clipping box (frontend-r22.27): the storey filter in
 * Modelindstillinger › Geometri no longer filters element lists; it sets
 * the clipping box's heights. "Kun etage N" keeps the storey from its floor
 * slab up to just under the next storey's floor slab; "Etage N og under"
 * keeps everything up to that same height. The box is scene-local (the
 * frame `ClippingBoxLayer` works in), the storeys and slabs are world
 * coordinates. The plan (Låst plan) cuts the same storey at the usual
 * plan height above its floor. Pure: no React, no DOM.
 */

import type { ClipBox, Corner } from '../components/bygning/settings/settingsState';
import type { StoreyFilterState } from '../viewport/elementFilter';
import { planCutHeight, storeyById, type Storey } from './storeys';

/** How far under the next storey's floor slab the box stops (metres): that slab is not drawn. */
export const STOREY_CLIP_GAP = 0.02;

/** One storey's heights as the clip needs them, world z. */
export interface StoreyLevel {
  id: number;
  /** Its floor (the storey's `zMin`). */
  floor: number;
  /** The underside of its floor slab: the lowest floor-slab base at or under the floor, else the floor. */
  bottom: number;
}

/** What `storeyLevels` reads of a slab (`SolidSlab`). */
export interface SlabLevel {
  storey_id: number | null;
  kind: 'floor' | 'roof';
  z_base: number;
}

/** A world-z range; an open end is ±Infinity. */
export interface ZRange {
  zMin: number;
  zMax: number;
}

/** The storeys as levels, lowest floor first; a storey's floor slabs (`GET /slabs`) lower its bottom. */
export function storeyLevels(storeys: readonly Pick<Storey, 'id' | 'zMin'>[], slabs?: readonly SlabLevel[] | null): StoreyLevel[] {
  const bottoms = new Map<number, number>();
  for (const s of slabs ?? []) {
    if (s.kind !== 'floor' || s.storey_id === null || !Number.isFinite(s.z_base)) continue;
    bottoms.set(s.storey_id, Math.min(bottoms.get(s.storey_id) ?? Infinity, s.z_base));
  }
  return storeys
    .filter((s) => Number.isFinite(s.zMin))
    .map((s) => {
      const slab = bottoms.get(s.id);
      // A "floor" slab above the storey's floor is not what it stands on.
      return { id: s.id, floor: s.zMin, bottom: slab !== undefined && slab <= s.zMin ? slab : s.zMin };
    })
    .sort((a, b) => a.floor - b.floor || a.id - b.id);
}

/**
 * The world-z range Etage keeps for storey `storeyId`: from its floor
 * slab's underside (`andBelow`: from the bottom of the model) up to just
 * under the next storey's floor slab (the top storey: up to the top of the
 * model, so its roof stays). Null for no storey or one not in `levels`.
 */
export function storeyZRange(levels: readonly StoreyLevel[], storeyId: number | null, andBelow: boolean): ZRange | null {
  if (storeyId === null) return null;
  const i = levels.findIndex((l) => l.id === storeyId);
  if (i < 0) return null;
  const next = levels[i + 1];
  return {
    zMin: andBelow ? -Infinity : levels[i].bottom - STOREY_CLIP_GAP,
    zMax: next ? next.bottom - STOREY_CLIP_GAP : Infinity,
  };
}

/**
 * The clipping box for a world-z range: z cut to the range made scene-local
 * (`origin` = the scene's world origin) and clamped to the model's
 * `bounds`, x and y the whole model. Null when the range misses the model.
 */
export function clipForZRange(bounds: ClipBox, origin: Corner, range: ZRange): ClipBox | null {
  const lo = Math.max(bounds.min.z, range.zMin - origin.z);
  const hi = Math.min(bounds.max.z, range.zMax - origin.z);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo > hi) return null;
  return { min: { ...bounds.min, z: lo }, max: { ...bounds.max, z: hi } };
}

/** The storey the plan cuts and the world height it cuts at (`planCutHeight`). */
export interface PlanScope {
  storeyId: number | null;
  cutZ: number | null;
}

/**
 * What the locked plan draws: the Etage filter's storey, else the focused
 * one (`?storey=` / a room's), cut at the plan height over its floor. No
 * storey: no cut (every storey's elements, as before).
 */
export function planScope(filter: StoreyFilterState, storeys: readonly Storey[], focusStoreyId: number | null): PlanScope {
  const storeyId = filter.storeyId ?? focusStoreyId;
  if (storeyId === null) return { storeyId: null, cutZ: null };
  const storey = storeyById(storeys, storeyId);
  return { storeyId, cutZ: storey ? planCutHeight(storey) : null };
}
